import { useCallback } from "react"
import { useFragment } from "react-relay"
import { type CustomMutationParams } from "@/hooks/useMutationWithErrorDialog"
import { formatDate } from "@/lib/format-date"
import { collectConsultationReportDocuments } from "@/modules/patients/detail/consultation-report/consultation-report-drawer"
import { useGenerateConsultationReportMutation } from "@/modules/patients/detail/consultation-report/services/useGenerateConsultationReportMutation"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { useChGenerateGeneralAttestationDocumentMutation } from "@/modules/patients/detail/general-attestation/ch/services/useChGenerateGeneralAttestationDocumentMutation"
import { useDeGenerateGeneralAttestationDocumentMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeGenerateGeneralAttestationDocumentMutation"
import { useChGenerateLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChGenerateLaboratoryPrescriptionMutation"
import { useChGenerateLegalGuardianDocumentMutation } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/useChGenerateLegalGuardianDocumentMutation"
import { useDeGenerateLegalGuardianAttestationDocumentMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeGenerateLegalGuardianAttestationDocumentMutation"
import { useChGenerateMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChGenerateMeasLaboratoryPrescriptionMutation"
import { useChGenerateRadiologyPrescriptionDocumentMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChGenerateRadiologyPrescriptionDocumentMutation"
import { useChGenerateNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChGenerateNeuropsychologicDiagnosticPrescriptionMutation"
import { useChGeneratePrescriptionMutation } from "@/modules/patients/detail/prescription/ch/services/useChGeneratePrescriptionMutation"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { useChGenerateSickLeaveDocumentMutation } from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChGenerateSickLeaveDocumentMutation"
import { useDeGenerateSickLeaveDocumentMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeGenerateSickLeaveDocumentMutation"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useChGenerateNutritionalCounsellingDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGenerateNutritionalCounsellingDocumentMutation"
import { useChGeneratePhysiotherapyDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGeneratePhysiotherapyDocumentMutation"
import { useChGeneratePsychotherapyDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGeneratePsychotherapyDocumentMutation"
import { useDeGenerateTherapyPrescriptionDocumentMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeGenerateTherapyPrescriptionDocumentMutation"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { CH_DOCUMENTS, DE_DOCUMENTS, ProgressNoteDocumentType } from "@/modules/patients/types/ProgressNoteDocument"

type GenerateDocumentFile = {
  variables: {
    id: string
    type: ProgressNoteDocumentType
    progressNoteId: string
    uploadSessionCode?: string
    medgateConnectOrderCode?: string
  }
  onStart?: () => void
  onSuccess?: (fileId: string) => void
  onError?: (fileId: string, message: string) => void
}

const params: CustomMutationParams = {
  shouldShowErrorDialog: false,
}

const useDocumentGeneration = () => {
  const countryCode = useCountryParam()
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const [generateChLegalGuardianDocument] = useChGenerateLegalGuardianDocumentMutation(params)
  const [generateChGeneralAttestationDocument] = useChGenerateGeneralAttestationDocumentMutation(params)
  const [generateChSickLeaveDocument] = useChGenerateSickLeaveDocumentMutation(params)
  const [generateDeSickLeaveDocument] = useDeGenerateSickLeaveDocumentMutation(params)
  const [generateChPhysiotherapyDocument] = useChGeneratePhysiotherapyDocumentMutation(params)
  const [generateChPrescriptionDocument] = useChGeneratePrescriptionMutation(params)
  const [generateNutritionalCounsellingDocument] = useChGenerateNutritionalCounsellingDocumentMutation(params)
  const [generateChRadiologyPrescriptionDocument] = useChGenerateRadiologyPrescriptionDocumentMutation(params)
  const [generateChMeasLaboratoryDocument] = useChGenerateMeasLaboratoryPrescriptionMutation(params)
  const [generateChNeuropsychologicDiagnosticPrescriptionDocument] =
    useChGenerateNeuropsychologicDiagnosticPrescriptionMutation(params)
  const [generateChLaboratoryPrescriptionDocument] = useChGenerateLaboratoryPrescriptionMutation(params)
  const [generateConsultationReportDocument] = useGenerateConsultationReportMutation()

  const [generateDeLegalGuardianDocument] = useDeGenerateLegalGuardianAttestationDocumentMutation()
  const [generateDeGeneralAttestationDocument] = useDeGenerateGeneralAttestationDocumentMutation()
  const [generateDeTherapyPrescriptionDocument] = useDeGenerateTherapyPrescriptionDocumentMutation()

  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const { conditionInformation: conditionInformationRef } = patientData.patientById!
  const { conditions: conditionList } = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    conditionInformationRef,
  )
  const progressNoteAndParentData = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)
  const progressNoteCreatedDate = useReferralStoreContext((state) => state.progressNoteCreatedDate)
  const consultationReportTimeToTreat = useConsultationReportStoreContext(
    (state) => state.consultationReportTimeToTreat,
  )
  const [generateChPsychotherapyDocument] = useChGeneratePsychotherapyDocumentMutation(params)
  const chProgressNotes = useDocumentStore((c) => c.chFormattedProgressNotes)
  const deProgressNotes = useDocumentStore((c) => c.deFormattedProgressNotes)

  const generateDocumentFile = useCallback(
    async ({
      variables: { id, type, progressNoteId, uploadSessionCode, medgateConnectOrderCode },
      onStart,
      onSuccess,
      onError,
    }: GenerateDocumentFile) => {
      onStart?.()

      if (countryCode === "CH") {
        // In general we should be exhaustive but enabling it here would bring LOT of additional types, maybe something should be revisited later...
        // oxlint-disable-next-line typescript/switch-exhaustiveness-check
        switch (type) {
          case CH_DOCUMENTS.GUARDIAN_ATTESTATION:
            generateChLegalGuardianDocument({
              params: {
                id,
              },
              onCompleted(response) {
                const fileId = response.ch_generateAttestationGuardianDocument.ch_AttestationGuardian?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          case CH_DOCUMENTS.GENERAL_ATTESTATION:
            generateChGeneralAttestationDocument({
              params: {
                id,
              },
              onCompleted(response) {
                const fileId = response.ch_generateGeneralAttestationDocument.ch_GeneralAttestation?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          case CH_DOCUMENTS.SICK_LEAVE:
            {
              const progressNoteIds = progressNoteAndParentData.flatMap((item) => item.episodeOfCareProgressNoteData.id)
              generateChSickLeaveDocument({
                params: {
                  id,
                  progressNoteIds,
                },
                onCompleted(response) {
                  const fileId = response.ch_generateSickLeaveDocument.ch_SickLeave?.fileId
                  if (fileId) {
                    onSuccess?.(fileId)
                  }
                },
              })
            }
            break
          case CH_DOCUMENTS.NUTRITIONAL_PRESCRIPTION:
            {
              const originalDocument = chProgressNotes?.get(progressNoteId)?.ch_nutritionalPrescription
              const diagnosisCode = originalDocument?.diagnosisCode
              const medicationIds = originalDocument?.medicationIds ?? []

              if (!diagnosisCode) {
                onError?.(id, "Not found diagnosisCode")
                return
              }

              generateNutritionalCounsellingDocument({
                id,
                diagnosisCode,
                medicationIds: [...medicationIds],
                onCompleted(response) {
                  const fileId = response.ch_generateNutritionalPrescriptionDocument.ch_NutritionalPrescription?.fileId
                  if (fileId) {
                    onSuccess?.(fileId)
                  }
                },
              })
            }
            break
          case CH_DOCUMENTS.PHYSIOTHERAPY:
            {
              const originalDocument = chProgressNotes?.get(progressNoteId)?.physiotherapy
              const conditions = originalDocument?.conditions
              const allergies = originalDocument?.allergies
              const diagnosisCode = originalDocument?.diagnosisCode
              if (!diagnosisCode) {
                onError?.(id, "Not found diagnosisCode")
                return
              }

              const conditionInfo =
                conditions && conditions.length > 0
                  ? conditionList
                      .filter((item) => conditions.includes(item.icdDiagnosis.diagnosisCode))
                      .map((item) => item.icdDiagnosis.diagnosisCode + " " + item.icdDiagnosis.text)
                  : []

              const allergyInfo =
                allergies && allergies.length > 0
                  ? conditionList
                      .filter((item) => allergies.includes(item.icdDiagnosis.diagnosisCode))
                      .map((item) => item.icdDiagnosis.diagnosisCode + " " + item.icdDiagnosis.text)
                  : []

              generateChPhysiotherapyDocument({
                id,
                diagnosisCode,
                conditions: conditionInfo,
                allergies: allergyInfo,
                onCompleted(response) {
                  const fileId =
                    response.ch_generatePhysiotherapyPrescriptionDocument.ch_PhysiotherapyPrescription?.fileId
                  if (fileId) {
                    onSuccess?.(fileId)
                  }
                },
              })
            }
            break
          case CH_DOCUMENTS.PRESCRIPTION: {
            generateChPrescriptionDocument(false, {
              id,
              progressNoteId,
              onCompleted(response) {
                const fileId = response.ch_generatePrescriptionDocument.ch_Prescription?.id
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          case CH_DOCUMENTS.PSYCHOLOGICAL_PSYCHOTHERAPY: {
            const originalDocument = chProgressNotes?.get(progressNoteId)?.psychologicalPsychotherapy
            const diagnosisCode = originalDocument?.diagnosisCode
            if (!diagnosisCode) {
              onError?.(id, "Not found diagnosisCode")
              return
            }

            generateChPsychotherapyDocument({
              id,
              diagnosisCode,
              onCompleted(response) {
                const fileId = response.ch_generatePsychotherapyPrescription.ch_PsychotherapyPrescription?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          case CH_DOCUMENTS.MEAS_RADIOLOGY_PRESCRIPTION: {
            const originalDocument = chProgressNotes?.get(progressNoteId)?.ch_measRadiologyPrescription
            const requestExaminations = originalDocument?.requestExaminations
            if (!requestExaminations) {
              onError?.(id, "Not found requestExaminations")
              return
            }

            generateChRadiologyPrescriptionDocument({
              id,
              requestExaminations,
              uploadSessionCode,
              onCompleted(response) {
                const fileId =
                  response.ch_generateMeasRadiologyPrescriptionDocument.ch_MeasRadiologyPrescription?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          case CH_DOCUMENTS.REFERRAL_LETTER:
            break
          case CH_DOCUMENTS.CONSULTATION_REPORT: {
            const originalDocument = chProgressNotes?.get(progressNoteId)?.ch_consultationReport
            if (!originalDocument) {
              onError?.(id, "Consultation report not found")
              return
            }

            const prescription = chProgressNotes?.get(progressNoteId)?.prescription
            const medications = (prescription?.medications ?? []).map((med) => ({
              name: med.name,
              morningDosage: med.morningDosage ?? 0,
              afternoonDosage: med.noonDosage ?? 0,
              eveningDosage: med.eveningDosage ?? 0,
              nightDosage: med.nightDosage ?? 0,
              unit: med.unit ?? null,
            }))

            const progressNote = chProgressNotes?.get(progressNoteId)
            const documentTypes = collectConsultationReportDocuments(progressNote)

            generateConsultationReportDocument({
              params: {
                id,
                diagnosis: originalDocument.diagnosisCode,
                dateOfConsultation: formatDate(progressNoteCreatedDate),
                timeToTreat: consultationReportTimeToTreat,
                medications,
                documents: documentTypes,
              },
              onCompleted(response) {
                const fileId = response.ch_generateConsultationReportDocument.ch_ConsultationReport?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          case CH_DOCUMENTS.MEAS_LABORATORY_PRESCRIPTION:
            generateChMeasLaboratoryDocument({
              id,
              uploadSessionCode,
              onCompleted(response) {
                const fileId =
                  response.ch_generateMeasLaboratoryPrescriptionDocument.ch_MeasLaboratoryPrescription?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          case CH_DOCUMENTS.NEUROPSYCHOLOGIC_DIAGNOSTIC_PRESCRIPTION: {
            generateChNeuropsychologicDiagnosticPrescriptionDocument({
              id,
              onCompleted(response) {
                const fileId =
                  response.ch_generateNeuropsychologicDiagnosticPrescription.ch_NeuropsychologicDiagnosticPrescription
                    ?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          case CH_DOCUMENTS.LABORATORY_PRESCRIPTION: {
            generateChLaboratoryPrescriptionDocument({
              id,
              medgateConnectOrderCode,
              onCompleted(response) {
                const fileId = response.ch_generateLaboratoryPrescriptionDocument.ch_LaboratoryPrescription?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          }
          default:
            break
        }
      } else if (countryCode === "DE") {
        // In general we should be exhaustive but enabling it here would bring LOT of additional types, maybe something should be revisited later...
        // oxlint-disable-next-line typescript/switch-exhaustiveness-check
        switch (type) {
          case DE_DOCUMENTS.SICK_LEAVE:
            generateDeSickLeaveDocument({
              params: {
                sickLeaveId: id,
              },
              onCompleted(response) {
                const fileId = response.de_generateSickLeaveDocument.de_SickLeave?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })

            break
          case DE_DOCUMENTS.GUARDIAN_ATTESTATION:
            generateDeLegalGuardianDocument({
              params: {
                id,
              },
              onCompleted(response) {
                const fileId = response.de_generateAttestationGuardianDocument.de_AttestationGuardian?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          case DE_DOCUMENTS.GENERAL_ATTESTATION:
            generateDeGeneralAttestationDocument({
              params: {
                id,
              },
              onCompleted(response) {
                const fileId = response.de_generateGeneralAttestationDocument.de_GeneralAttestation?.fileId
                if (fileId) {
                  onSuccess?.(fileId)
                }
              },
            })
            break
          case DE_DOCUMENTS.THERAPY_PRESCRIPTION:
            {
              const originalDocument = deProgressNotes?.get(progressNoteId)?.therapyPrescription
              const diagnosisCode = originalDocument?.diagnosisCode
              if (!diagnosisCode) {
                onError?.(id, "Not found diagnosisCode")
                return
              }

              generateDeTherapyPrescriptionDocument({
                id,
                diagnosisCode,
                onCompleted(response) {
                  const fileId = response.de_generateTherapyPrescriptionDocument.de_TherapyPrescription?.fileId
                  if (fileId) {
                    onSuccess?.(fileId)
                  }
                },
              })
            }
            break
          case DE_DOCUMENTS.REFERRAL_LETTER:
            break
          default:
            break
        }
      }
    },
    [
      countryCode,
      generateChLegalGuardianDocument,
      generateChGeneralAttestationDocument,
      progressNoteAndParentData,
      generateChSickLeaveDocument,
      chProgressNotes,
      generateNutritionalCounsellingDocument,
      conditionList,
      generateChPhysiotherapyDocument,
      generateChPrescriptionDocument,
      generateChPsychotherapyDocument,
      generateChRadiologyPrescriptionDocument,
      generateChMeasLaboratoryDocument,
      generateChNeuropsychologicDiagnosticPrescriptionDocument,
      generateChLaboratoryPrescriptionDocument,
      generateConsultationReportDocument,
      progressNoteCreatedDate,
      consultationReportTimeToTreat,
      generateDeSickLeaveDocument,
      deProgressNotes,
      generateDeLegalGuardianDocument,
      generateDeGeneralAttestationDocument,
      generateDeTherapyPrescriptionDocument,
    ],
  )

  return generateDocumentFile
}

export { useDocumentGeneration }
